export type Theme = 'light' | 'dark';

export interface AppState {
  theme: Theme;
  searchOpen: boolean;
  route: string;
}

const STORAGE_KEY = 'llmviz:theme';
const OUTER_THEME_STORAGE_KEY = 'theme-storage';

type OuterThemeStore = {
  getState: () => { theme: 'light' | 'dark' | 'system' };
  subscribe: (listener: () => void) => () => void;
};

function getParentWindow(): Window | null {
  if (typeof window === 'undefined') return null;
  if (window.parent === window) return null;
  try {
    void window.parent.document;
    return window.parent;
  } catch {
    return null;
  }
}

function resolveOuterTheme(theme: 'light' | 'dark' | 'system'): Theme {
  if (theme === 'system') {
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  }
  return theme;
}

function readOuterTheme(): Theme | null {
  const parent = getParentWindow();
  if (!parent) return null;
  try {
    const raw = parent.localStorage.getItem(OUTER_THEME_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const state = parsed?.state as { theme?: 'light' | 'dark' | 'system' } | undefined;
    if (state?.theme === 'light' || state?.theme === 'dark' || state?.theme === 'system') {
      return resolveOuterTheme(state.theme);
    }
  } catch {
    // Ignore cross-frame storage errors.
  }
  return null;
}

function getOuterStore(): OuterThemeStore | null {
  const parent = getParentWindow();
  if (!parent) return null;
  try {
    const w = parent as unknown as { useThemeStore?: OuterThemeStore };
    return typeof w.useThemeStore?.getState === 'function' ? w.useThemeStore : null;
  } catch {
    return null;
  }
}

function getInitialTheme(): Theme {
  const outer = readOuterTheme();
  if (outer) return outer;
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
  }
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
}

const state: AppState = {
  theme: getInitialTheme(),
  searchOpen: false,
  route: typeof window !== 'undefined' ? window.location.hash : ''
};

const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((listener) => listener());
}

export function getState(): AppState {
  return state;
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setTheme(theme: Theme): void {
  if (state.theme === theme) return;
  state.theme = theme;
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', theme);
  }
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, theme);
  }
  notify();
}

export function toggleTheme(): void {
  setTheme(state.theme === 'light' ? 'dark' : 'light');
}

export function setSearchOpen(open: boolean): void {
  state.searchOpen = open;
  notify();
}

export function navigateTo(hash: string): void {
  if (typeof window !== 'undefined') {
    window.location.hash = hash;
  }
}

export function navigateBack(): void {
  if (typeof window !== 'undefined') {
    window.history.back();
  }
}

export function updateRouteFromHash(): void {
  if (typeof window !== 'undefined') {
    state.route = window.location.hash;
    notify();
  }
}

export function initState(): void {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', state.theme);
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('hashchange', updateRouteFromHash);
    updateRouteFromHash();
    attachParentThemeSync();
  }
}

function attachParentThemeSync(): void {
  const outerStore = getOuterStore();
  if (outerStore) {
    try {
      outerStore.subscribe(() => {
        const outerTheme = outerStore.getState().theme;
        const resolved = resolveOuterTheme(outerTheme);
        if (resolved !== state.theme) {
          state.theme = resolved;
          if (typeof document !== 'undefined') {
            document.documentElement.setAttribute('data-theme', resolved);
          }
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem(STORAGE_KEY, resolved);
          }
          notify();
        }
      });
    } catch {
      // Ignore subscription errors.
    }
  }

  const parent = getParentWindow();
  if (!parent) return;
  try {
    const observer = new MutationObserver(() => {
      const parentTheme = parent.document.documentElement.getAttribute('data-theme');
      if (parentTheme === 'light' || parentTheme === 'dark') {
        if (parentTheme !== state.theme) {
          state.theme = parentTheme;
          if (typeof document !== 'undefined') {
            document.documentElement.setAttribute('data-theme', parentTheme);
          }
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem(STORAGE_KEY, parentTheme);
          }
          notify();
        }
      }
    });
    observer.observe(parent.document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
  } catch {
    // Ignore cross-frame observer errors.
  }
}
